import { PolicyApiSdk, ClientInferResponses, contract } from '@pie/policy-api-sdk';
import { useQuery } from '@tanstack/react-query';
import { env } from '@/config/env';
import { ApiError } from '@/lib/api/ApiError';
import { POST_ISSUANCE_LIFECYCLE_STATUSES } from './usePolicy';
import { useAuthContext } from '@/components/AuthContext';

/*
 * Types
 */
type SearchPolicy = Extract<
  ClientInferResponses<typeof contract.searchPolicies>,
  { status: 200 }
>['body']['results'][number];

export type IssuedSearchPolicy = Omit<SearchPolicy, 'lifecycleStatus' | 'policyNumber'> & {
  lifecycleStatus: (typeof POST_ISSUANCE_LIFECYCLE_STATUSES)[number];
  policyNumber: string;
};

const { client } = new PolicyApiSdk({ environment: env.NEXT_PUBLIC_PIE_ENV });

export const useSearchPolicies = () => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery({
    queryFn: async () => {
      const headers = await getSessionHeaders();
      const response = await client.searchPolicies({
        headers,
        query: {
          limit: 100,
        },
      });

      if (response.status !== 200) {
        throw new ApiError(response.status, response.body, response.headers);
      }

      const results = response.body.results;

      if (!results.every(isIssuedSearchPolicy)) {
        throw new Error('These policies are not available to view at this time.');
      }

      // need to reconstruct to assure type narrowing for results
      return { ...response.body, results };
    },
    queryKey: ['policyApiSdk', 'searchPolicies'],
  });
};

/*
 * Derived
 */

const isIssuedSearchPolicy = (policy: SearchPolicy): policy is IssuedSearchPolicy => {
  return (
    POST_ISSUANCE_LIFECYCLE_STATUSES.includes(
      policy.lifecycleStatus as IssuedSearchPolicy['lifecycleStatus']
    ) && policy.policyNumber !== null
  );
};
